import React, { ReactNode, useState } from 'react';
import {
  Box,
  CardHeader,
  Collapse,
  Typography,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ExpandMore } from 'toolkit/info-card/InfoCard';
import { themeColors } from 'toolkit/styles/colors';
import { PencilSimpleIcon } from '@phosphor-icons/react';
import { ReemaThemeV2 } from '@act/storybook';

export interface CollapsibleHistoryCardProps {
  title: string;
  subtitle: string;
  expandedContent?: ReactNode;
  showEditButton?: boolean;
  onEdit?: () => void;
  icon?: ReactNode;
  defaultExpanded?: boolean;
}

export const CollapsibleHistoryCard: React.FC<CollapsibleHistoryCardProps> = ({
  title,
  subtitle,
  expandedContent,
  showEditButton = false,
  onEdit,
  icon,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const hasExpandableContent = Boolean(expandedContent);

  const handleExpandClick = () => {
    if (hasExpandableContent) {
      setExpanded(!expanded);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.();
  };

  return (
    <Box
      sx={{
        padding: '16px',
        borderRadius: '8px',
        border: `1px solid ${ReemaThemeV2.LIGHT_MODE.palette.strokePrimary}`,
        gap: '16px',
        backgroundColor: 'white',
      }}
    >
      <CardHeader
        title={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              {icon && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& svg': {
                      width: '20px',
                      height: '20px',
                      color: '#666',
                    },
                  }}
                >
                  {icon}
                </Box>
              )}
              <Typography variant="titleMedium">{title}</Typography>
              {!expanded && (
                <Typography variant="bodySmall">{subtitle}</Typography>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {showEditButton && (
                <IconButton
                  size="small"
                  onClick={handleEditClick}
                  aria-label="Edit"
                >
                  <PencilSimpleIcon
                    color={ReemaThemeV2.LIGHT_MODE.palette.blueShades[600]}
                    size={24}
                    weight="bold"
                  />
                </IconButton>
              )}
              {hasExpandableContent && (
                <ExpandMore
                  expand={expanded}
                  onClick={handleExpandClick}
                  aria-expanded={expanded}
                  aria-label="show more"
                  sx={{ marginTop: '-40px', marginBottom: '-40px' }}
                >
                  <ExpandMoreIcon
                    sx={{ color: themeColors.onSurfaceVariant }}
                  />
                </ExpandMore>
              )}
            </Box>
          </Box>
        }
        subheader={''}
        sx={{ padding: '0px 0 8px' }}
      />
      {expandedContent && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {expandedContent}
        </Collapse>
      )}
    </Box>
  );
};

export default CollapsibleHistoryCard;
